import React from "react";
import { getGlobalRange } from "./functions/period-store";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import PubSub from 'pubsub-js';
import { campaignsDashboardOnly, sellsThroughReps, hasTenantFeature } from './functions/tenant-features';

// Dashboard-only campaign tenants (managed, read-only Overview + Markets) must
// never reach campaign details, flights, the flight builder, or provider
// settings — a typed/stale URL to any of those redirects to the overview.
const guardCampaignAction = el => campaignsDashboardOnly() ? /*#__PURE__*/React.createElement(Redirect, {
  to: "/Campaigns"
}) : el;
import { autopilotHiddenHere, manageDataLegacyHere } from "./functions/tenant-features";
import { section } from "./components/section-access";
import FeedsKitView from "./views/manage-data/feeds";
import ConnectKitViewComponent from "./views/manage-data/connect";
import Main from "./layout/main";
import Grid from "./layout/grid";
import AppLayout from './layout/app-layout';
import AppSidebar from './layout/sidebar';
import { ThemeProvider } from './theme/theme-provider';
import { DashboardViewComponent } from './views/dashboard';
import HomeGateComponent from './views/onboarding/home-gate';
import PlanGate from './layout/plan-gate';
import { WelcomeViewComponent } from './views/onboarding/welcome';
import { SetupViewComponent } from './views/onboarding/setup';
import { EmailImportViewComponent } from './views/onboarding/email-import';
import { TrackingSetupViewComponent } from './views/onboarding/tracking-setup';
import { ShopifyConnectViewComponent } from './views/onboarding/shopify-connect';
import { ManageConnectionsViewComponent } from './views/manage-connections';
import { ManageShopifyViewComponent } from './views/manage-shopify';
import TerritoryViewComponent from './views/sales/territory';
import TerritoryDetailViewComponent from './views/sales/territory-detail';
import ProductsWorkspaceComponent from './views/products/workspace';
import FamilyDetailComponent from './views/products/family-detail';
import StoresViewComponent from './views/stores';
import StoreDetailViewComponent from './views/stores/detail';
import StoreTargetsViewComponent from './views/stores/targets';
import DealersViewComponent from './views/dealers';
import DealerDetailViewComponent from './views/dealers/detail';
import DealerTargetsViewComponent from './views/dealers/targets';
import SalesExecutiveViewComponent from './views/sales/executive';
import SalesTargetsViewComponent from './views/sales/targets';
import TerritoriesKitView from './views/manage-data/territories';
import QuotesListViewComponent from './views/quotes';
import QuoteEditorViewComponent from './views/quotes/editor';
import QuoteOrdersViewComponent from './views/quotes/orders';
import PriceTiersViewComponent from './views/manage-data/price-tiers';
import SalesDigestsViewComponent from './views/manage-data/sales-digests';
import ConfigRulesViewComponent from './views/config-rules';
import TrackingSitesViewComponent from './views/tracking-sites';
import RepDetailViewComponent from './views/sales/rep-detail';
import RepDealerProductsComponent from './views/sales/rep-dealer-products';
import RepCompareComponent from './views/sales/rep-compare';
import ChannelDetailViewComponent from './views/sales/channel-detail';
import AppsViewComponent from './views/apps';
import { DataEnrichmentViewComponent } from './views/data-enrichment';
import { ConnectDataViewComponent } from './views/data-feeds/connect';
import SiteMonitorViewComponent from './views/site-monitor';
import AiVisibilityViewComponent from './views/ai-visibility';
import AdCampaignsViewComponent from './views/campaigns';
import CampaignsOverviewViewComponent from "./views/campaigns/overview";
import AutopilotViewComponent from "./views/campaigns/autopilot";
import AutopilotRulesComponent from "./views/campaigns/autopilot-rules";
import CampaignMarketsViewComponent from "./views/campaigns/markets";
import AdCampaignDetailViewComponent from './views/campaigns/detail';
import ProviderSettingsViewComponent from './views/campaigns/provider-settings';
import FlightDetailViewComponent from './views/campaigns/flight-detail';
import FlightBuilderViewComponent from './views/campaigns/flight-builder';
import CampaignEditViewComponent from './views/campaigns/campaign-edit';
import EmailAutomationViewComponent from './views/email-automation';
import EmailAutomationDetailViewComponent from './views/email-automation/detail';
import EmailAutomationEditorComponent from './views/email-automation/editor';
import EmailJourneysViewComponent from './views/email-journeys';
import EmailJourneyDetailViewComponent from './views/email-journeys/detail';
import EmailJourneyEditorComponent from './views/email-journeys/editor';
import EmailCampaignsViewComponent from './views/email-campaigns';
import EmailCampaignWizardComponent from './views/email-campaigns/wizard';
import EmailCampaignDetailViewComponent from './views/email-campaigns/detail';
import EmailSendersViewComponent from './views/email-campaigns/senders';
import OutreachWizardComponent from './views/outreach/wizard';
import OutreachHomeComponent from './views/outreach';
import OutreachDetailComponent from './views/outreach/detail';
import OutreachInfraComponent from './views/outreach/infrastructure';
import OutreachRepliesComponent from './views/outreach/replies';
import CustomerDetailViewComponent from './views/sales/customer-detail';
import ProductDetailViewComponent from './views/sales/product-detail';
import ProductPerformanceViewComponent from './views/product-performance';
import { ProductConfigurationsViewComponent } from './views/product-configurations';
import { SocialStatsViewComponent } from './views/social';
import { MetaOAuthCallbackViewComponent } from './views/oauth/meta-callback';
import { SocialOAuthCallbackViewComponent } from './views/oauth/social-callback';
import { ContentHubViewComponent } from './views/content';
import { ContentBuilderViewComponent } from './views/content/builder';
import { ImageWorkspaceViewComponent } from './views/content/image-workspace';
import { SequenceWorkspaceViewComponent } from './views/content/sequence-workspace';
import ClipWorkspaceViewComponent from './views/content/clip-workspace';
import SequenceNewViewComponent from './views/content/sequence-new';
import StoryNewViewComponent from './views/content/story-new';
import { CampaignReviewViewComponent } from './views/campaign-review';
import LifecycleViewComponent from './views/lifecycle';
import KeywordsViewComponent from './views/keywords';
import CustomersViewComponent from './views/customers';
import InventoryPlannerComponent from './views/inventory';
import PrivacyPolicyView from './views/privacy';
import ManageDataViewComponent from './views/data-feeds';
import CustomerAccountDetailComponent from './views/customers/detail';
import KeywordDetailViewComponent from './views/keywords/detail';
import { ContactsViewComponent } from './views/contacts/index-v2';
import { SearchViewComponent } from './views/search';
import { ContactDetailViewComponent } from './views/contacts/detail-v2';
import { AudienceViewComponent } from './views/manage-audience';
import { SettingsViewComponent } from './views/settings';
import { BrandKitViewComponent } from "./views/settings/brand-kit";
import { TemplatesSettingsViewComponent } from "./views/settings/templates";
import ActivityLogViewComponent from './views/settings/activity-log';
import AudiencesViewComponent from './views/audiences';
import AudienceDetailViewComponent from './views/audiences/detail';
import AudienceDuplicatesViewComponent from './views/audiences/duplicates';
import AudienceSuppressionViewComponent from './views/audiences/suppression';
import AudienceHealthViewComponent from './views/audiences/health';
import { AudienceCreateViewComponent } from './views/manage-audience/create';
import { ProductViewComponent } from './views/manage-product';
import { UpdateProductViewComponent } from './views/manage-product/manage';
import { ActivityViewComponent } from './views/activity';
import WebActivityViewComponent from './views/web-activity';
import WebActivityDetailComponent from './views/web-activity/detail';
import WebActivityKeyValuesComponent from './views/web-activity/keyvalues';
import { ActivityDetailViewComponent } from './views/activity/detail';
import { ActivityPagesViewComponent } from "./views/activity/pages";
import { ActivityProductViewComponent } from "./views/activity/product";
import FunnelsViewComponent from "./views/funnels";
import HeatViewComponent from "./views/heat";
import { ActivityUtmViewComponent } from "./views/activity/utm";
import { ActivityKeyValuesViewComponent } from "./views/activity/keyvalues";
import { ActivityLocationViewComponent } from "./views/activity/location";
import { LocationViewComponent } from './views/manage-location';
import { UploadLocationViewComponent } from './views/manage-location/upload';
import { ForecastProductViewComponent } from "./views/forecast/product";
import { ForecastProductDetailViewComponent } from "./views/forecast/product-detail";
import { ForecastSimilarityViewComponent } from "./views/forecast/similarity";
import { ForecastSimilarityDetailViewComponent } from "./views/forecast/similarity-detail";
import { NotificationsViewComponent } from './views/notifications';
import { CompanyAnalysisViewComponent } from "./views/research/company";
import { CompanyDetailViewComponent } from "./views/research/company-detail";
import { CompanyAnalysisCreateViewComponent } from "./views/research/create-analysis";
import { ResearchAssistantViewComponent } from "./views/research/assistant";
import { ResearchAssistantDetailViewComponent } from "./views/research/assistant-detail";
import { ResearchAssistantCreateViewComponent } from "./views/research/create-assistant";
import { MarketTrendsViewComponent } from "./views/research/market-trends";
import { MarketTrendsDetailViewComponent } from "./views/research/market-trends-detail";
import { MarketTrendsCreateViewComponent } from "./views/research/create-market-trends";
import { SalesAnalysisViewComponent } from "./views/sales/analysis";
import { MarketIntelligenceView } from "./views/market-intelligence";
import { ReportsListViewComponent } from "./views/reports";
import { ReportViewerViewComponent } from "./views/reports/viewer";
import { ReportBuilderViewComponent } from "./views/reports/builder";
import SignInComponent from "./views/auth/sign-in";
import Auth from "./functions/authenticate";
import SignOutComponent from "./views/auth/sign-out";
import Alert from "./alert";
import AssistantPanel from "./components/assistant-panel";
import { ModulePage } from "./components/tenant-modules";
class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      route: ''
    };
  }
  componentDidMount() {
    //this.subscribers();
    // Shopify landing with a different broker login than the cached one → adopt it and reload.
    Auth.adoptBrokerIdentityIfDifferent().then(changed => {
      if (changed) location.reload();
    }).catch(() => {});
  }
  render() {
    var showAppLayout = true;
    var path = location.pathname;
    var mainCss = 'dashboard';
    if (path.toLowerCase() == '/auth/signin' || path.toLowerCase() == '/auth/signout') {
      showAppLayout = false;
      console.log('auth');
      mainCss = 'main-signin';
    }
    return /*#__PURE__*/React.createElement(ThemeProvider, null, /*#__PURE__*/React.createElement(Router, null, /*#__PURE__*/React.createElement(PlanGate, null), showAppLayout && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(AppLayout, {
      authenticated: true,
      search: this.state.search
    }), /*#__PURE__*/React.createElement(AppSidebar, null)), /*#__PURE__*/React.createElement(Main, {
      cls: mainCss
    }, /*#__PURE__*/React.createElement(Grid, null, /*#__PURE__*/React.createElement(Switch, null, /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/",
      render: r => /*#__PURE__*/React.createElement(HomeGateComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Welcome",
      render: r => /*#__PURE__*/React.createElement(WelcomeViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Setup",
      render: r => /*#__PURE__*/React.createElement(SetupViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Setup/Email",
      render: r => /*#__PURE__*/React.createElement(EmailImportViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Tracking",
      render: r => /*#__PURE__*/React.createElement(TrackingSetupViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Shopify",
      render: r => /*#__PURE__*/React.createElement(ShopifyConnectViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Manage/Connections",
      render: r => /*#__PURE__*/React.createElement(ManageConnectionsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Manage/Shopify",
      render: r => /*#__PURE__*/React.createElement(ManageShopifyViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory",
      render: r => section('sales', /*#__PURE__*/React.createElement(TerritoryViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range",
      render: r => section('sales', /*#__PURE__*/React.createElement(TerritoryViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/region/:region",
      render: r => section('sales', /*#__PURE__*/React.createElement(TerritoryDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/rep/:repCode",
      render: r => section('sales', /*#__PURE__*/React.createElement(RepDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/rep/:repCode/dealers",
      render: r => section('sales', /*#__PURE__*/React.createElement(RepDealerProductsComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/rep/:repCode/compare",
      render: r => section('sales', /*#__PURE__*/React.createElement(RepCompareComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/channel/:channel",
      render: r => section('sales', /*#__PURE__*/React.createElement(ChannelDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/customer/:id",
      render: r => section('sales', /*#__PURE__*/React.createElement(CustomerDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Territory/:range/product/:id",
      render: r => section('sales', /*#__PURE__*/React.createElement(ProductDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Families",
      render: () => /*#__PURE__*/React.createElement(Redirect, {
        to: `/Products/${getGlobalRange("this_month")}?grain=families`
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Families/:range",
      render: r => /*#__PURE__*/React.createElement(Redirect, {
        to: `/Products/${r.match.params.range}?grain=families`
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Products/:range/product/:id",
      render: r => /*#__PURE__*/React.createElement(ProductDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Products",
      render: r => /*#__PURE__*/React.createElement(ProductsWorkspaceComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Products/:range",
      render: r => /*#__PURE__*/React.createElement(ProductsWorkspaceComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Products/:range/family/:family",
      render: r => /*#__PURE__*/React.createElement(FamilyDetailComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Stores",
      render: r => section('sales', /*#__PURE__*/React.createElement(StoresViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Stores/targets",
      render: r => section('sales', /*#__PURE__*/React.createElement(StoreTargetsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Stores/:range",
      render: r => section('sales', /*#__PURE__*/React.createElement(StoresViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Stores/:range/store/:storeId",
      render: r => section('sales', /*#__PURE__*/React.createElement(StoreDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Dealers",
      render: () => section('sales', /*#__PURE__*/React.createElement(Redirect, {
        to: `/Sales/${getGlobalRange("this_year")}/dealers`
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Dealers/targets",
      render: () => section('sales', /*#__PURE__*/React.createElement(Redirect, {
        to: "/Sales/targets?scope=dealer"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Dealers/:range",
      render: r => section('sales', /*#__PURE__*/React.createElement(Redirect, {
        to: `/Sales/${r.match.params.range}/dealers`
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Dealers/:range/dealer/:dealerId",
      render: r => section('sales', /*#__PURE__*/React.createElement(Redirect, {
        to: `/Territory/${r.match.params.range}/customer/${r.match.params.dealerId}`
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Quotes",
      render: r => section('sales', /*#__PURE__*/React.createElement(QuotesListViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Quotes/orders",
      render: r => section('sales', /*#__PURE__*/React.createElement(QuoteOrdersViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Quotes/orders/:id",
      render: r => section('sales', /*#__PURE__*/React.createElement(QuoteOrdersViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Quotes/new",
      render: r => section('sales', /*#__PURE__*/React.createElement(QuoteEditorViewComponent, {
        params: {}
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Quotes/:id",
      render: r => section('sales', /*#__PURE__*/React.createElement(QuoteEditorViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data/PriceTiers",
      render: r => sellsThroughReps() && hasTenantFeature("quotes") ? /*#__PURE__*/React.createElement(PriceTiersViewComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Data"
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data/Digests",
      render: r => /*#__PURE__*/React.createElement(SalesDigestsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data/Rules",
      render: r => /*#__PURE__*/React.createElement(ConfigRulesViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data/Tracking",
      render: r => /*#__PURE__*/React.createElement(TrackingSitesViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data/Territories",
      render: r => sellsThroughReps() && hasTenantFeature("territoryRep") ? /*#__PURE__*/React.createElement(TerritoriesKitView, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Data"
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Apps",
      render: r => /*#__PURE__*/React.createElement(AppsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/DataEnrichment",
      render: r => /*#__PURE__*/React.createElement(DataEnrichmentViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Seo",
      render: r => /*#__PURE__*/React.createElement(SiteMonitorViewComponent, {
        kind: "seo",
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Ada",
      render: r => /*#__PURE__*/React.createElement(SiteMonitorViewComponent, {
        kind: "ada",
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/AiVisibility",
      render: r => /*#__PURE__*/React.createElement(AiVisibilityViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns",
      render: r => section('marketing', /*#__PURE__*/React.createElement(CampaignsOverviewViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/Flights",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(AdCampaignsViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/Markets",
      render: r => section('marketing', /*#__PURE__*/React.createElement(CampaignMarketsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/Autopilot",
      render: () => section('marketing', /*#__PURE__*/React.createElement(Redirect, {
        to: "/Autopilot"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Autopilot",
      render: r => section('marketing', autopilotHiddenHere() ? /*#__PURE__*/React.createElement(Redirect, {
        to: "/"
      }) : /*#__PURE__*/React.createElement(AutopilotViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Autopilot/rules",
      render: r => section('marketing', autopilotHiddenHere() ? /*#__PURE__*/React.createElement(Redirect, {
        to: "/"
      }) : /*#__PURE__*/React.createElement(AutopilotRulesComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Autopilot/:lane(campaigns|email|content|markets)",
      render: r => section('marketing', autopilotHiddenHere() ? /*#__PURE__*/React.createElement(Redirect, {
        to: "/"
      }) : /*#__PURE__*/React.createElement(AutopilotViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/Markets/:market",
      render: r => section('marketing', /*#__PURE__*/React.createElement(CampaignMarketsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/detail/:id",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(AdCampaignDetailViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/EditCampaign/:id",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(CampaignEditViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/Settings",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(ProviderSettingsViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/NewFlight",
      render: () => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(FlightBuilderViewComponent, {
        params: {}
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/FlightBuilder",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(FlightBuilderViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/FlightBuilder/:id",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(FlightBuilderViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/EditFlight/:editId",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(FlightBuilderViewComponent, {
        params: {
          id: r.match.params.editId
        }
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Campaigns/flight/:id",
      render: r => section('marketing', guardCampaignAction(/*#__PURE__*/React.createElement(FlightDetailViewComponent, {
        params: r.match.params
      })))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/automations", "/EmailAutomation"],
      render: () => /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/journeys"
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/automations/detail/:id", "/EmailAutomation/detail/:id"],
      render: r => /*#__PURE__*/React.createElement(EmailAutomationDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/automations/new", "/EmailAutomation/new"],
      render: () => /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/journeys/new?template=single"
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/automations/edit/:id", "/EmailAutomation/edit/:id"],
      render: r => /*#__PURE__*/React.createElement(EmailAutomationEditorComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/journeys", "/EmailAutomation/journeys"],
      render: r => /*#__PURE__*/React.createElement(EmailJourneysViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/journeys/new", "/EmailAutomation/journeys/new", "/Email/journeys/edit/:id", "/EmailAutomation/journeys/edit/:id"],
      render: r => /*#__PURE__*/React.createElement(EmailJourneyEditorComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/journeys/detail/:id", "/EmailAutomation/journeys/detail/:id"],
      render: r => /*#__PURE__*/React.createElement(EmailJourneyDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email", "/Email/campaigns", "/EmailAutomation/campaigns"],
      render: r => /*#__PURE__*/React.createElement(EmailCampaignsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/campaigns/new", "/EmailAutomation/campaigns/new", "/Email/campaigns/edit/:id", "/EmailAutomation/campaigns/edit/:id"],
      render: r => /*#__PURE__*/React.createElement(EmailCampaignWizardComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/campaigns/detail/:id", "/EmailAutomation/campaigns/detail/:id"],
      render: r => /*#__PURE__*/React.createElement(EmailCampaignDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/settings", "/EmailAutomation/settings", "/Email/senders", "/EmailAutomation/senders"],
      render: r => /*#__PURE__*/React.createElement(EmailSendersViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Email/campaigns/outreach/new", "/Email/campaigns/outreach/:id/edit"],
      render: r => hasTenantFeature('outreach') ? /*#__PURE__*/React.createElement(OutreachWizardComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/campaigns"
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/campaigns/outreach/:id",
      render: r => section('marketing', hasTenantFeature('outreach') ? /*#__PURE__*/React.createElement(OutreachDetailComponent, {
        key: r.match.params.id,
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/campaigns"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/senders/outreach",
      render: r => section('marketing', hasTenantFeature('outreach') ? /*#__PURE__*/React.createElement(OutreachInfraComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/campaigns"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/outreach",
      render: r => section('marketing', hasTenantFeature('outreach') ? /*#__PURE__*/React.createElement(OutreachHomeComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/campaigns"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/outreach/replies",
      render: r => section('marketing', hasTenantFeature('outreach') ? /*#__PURE__*/React.createElement(OutreachRepliesComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/campaigns"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/outreach/infrastructure",
      render: () => section('marketing', /*#__PURE__*/React.createElement(Redirect, {
        to: "/Email/senders/outreach"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/outreach/edit/:id",
      render: r => section('marketing', /*#__PURE__*/React.createElement(Redirect, {
        to: `/Email/campaigns/outreach/${r.match.params.id}/edit`
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Email/outreach/detail/:id",
      render: r => section('marketing', /*#__PURE__*/React.createElement(Redirect, {
        to: `/Email/campaigns/outreach/${r.match.params.id}`
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/CampaignReview",
      render: r => section('marketing', /*#__PURE__*/React.createElement(CampaignReviewViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/build/new",
      render: r => section('marketing', /*#__PURE__*/React.createElement(StoryNewViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/build/:storyId",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ContentBuilderViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/images",
      render: () => section('marketing', /*#__PURE__*/React.createElement(Redirect, {
        to: "/Content/images/new"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/images/new",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ImageWorkspaceViewComponent, {
        params: {}
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/images/:id",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ImageWorkspaceViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/clips",
      render: () => section('marketing', /*#__PURE__*/React.createElement(Redirect, {
        to: "/Content/clips/new"
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/clips/:id",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ClipWorkspaceViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/sequences/new",
      render: r => section('marketing', /*#__PURE__*/React.createElement(SequenceNewViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/sequences/:id",
      render: r => section('marketing', /*#__PURE__*/React.createElement(SequenceWorkspaceViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ContentHubViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Content/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ContentHubViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/OAuth/Meta",
      render: r => /*#__PURE__*/React.createElement(MetaOAuthCallbackViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/OAuth/:provider",
      render: r => /*#__PURE__*/React.createElement(SocialOAuthCallbackViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Social",
      render: r => section('marketing', /*#__PURE__*/React.createElement(SocialStatsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Social/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(SocialStatsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Configurations",
      render: r => /*#__PURE__*/React.createElement(ProductConfigurationsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Configurations/:range",
      render: r => /*#__PURE__*/React.createElement(ProductConfigurationsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/ProductPerformance",
      render: r => /*#__PURE__*/React.createElement(ProductPerformanceViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/ProductPerformance/:range",
      render: r => /*#__PURE__*/React.createElement(ProductPerformanceViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Lifecycle",
      render: r => /*#__PURE__*/React.createElement(LifecycleViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Lifecycle/:range",
      render: r => /*#__PURE__*/React.createElement(LifecycleViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Privacy",
      render: () => /*#__PURE__*/React.createElement(PrivacyPolicyView, null)
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data",
      render: r => manageDataLegacyHere() ? /*#__PURE__*/React.createElement(ManageDataViewComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(FeedsKitView, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Data/Connect",
      render: r => manageDataLegacyHere() ? /*#__PURE__*/React.createElement(ConnectDataViewComponent, {
        params: r.match.params
      }) : /*#__PURE__*/React.createElement(ConnectKitViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Customers",
      render: r => /*#__PURE__*/React.createElement(CustomersViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Customers/account/:id",
      render: r => /*#__PURE__*/React.createElement(CustomerAccountDetailComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Customers/:range",
      render: r => /*#__PURE__*/React.createElement(CustomersViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Inventory",
      render: r => /*#__PURE__*/React.createElement(InventoryPlannerComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Keywords",
      render: r => /*#__PURE__*/React.createElement(KeywordsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Keywords/:id",
      render: r => /*#__PURE__*/React.createElement(KeywordDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Auth/SignIn",
      render: r => /*#__PURE__*/React.createElement(SignInComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Auth/SignOut",
      render: r => /*#__PURE__*/React.createElement(SignOutComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Contacts",
      render: r => /*#__PURE__*/React.createElement(ContactsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Contacts/:range",
      render: r => /*#__PURE__*/React.createElement(ContactsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Contacts/Detail/:id",
      render: r => /*#__PURE__*/React.createElement(ContactDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Contacts/Detail/:id/:range",
      render: r => /*#__PURE__*/React.createElement(ContactDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Contacts/Audience/:audience",
      render: r => /*#__PURE__*/React.createElement(ContactsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Contacts/Audience/:audience/:range",
      render: r => /*#__PURE__*/React.createElement(ContactsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audience",
      render: r => /*#__PURE__*/React.createElement(AudienceViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audiences",
      render: r => /*#__PURE__*/React.createElement(AudiencesViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audiences/Duplicates",
      render: r => /*#__PURE__*/React.createElement(AudienceDuplicatesViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audiences/Suppression",
      render: r => /*#__PURE__*/React.createElement(AudienceSuppressionViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audiences/Health",
      render: r => /*#__PURE__*/React.createElement(AudienceHealthViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audiences/:range",
      render: r => /*#__PURE__*/React.createElement(AudiencesViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audiences/Detail/:id/:range",
      render: r => /*#__PURE__*/React.createElement(AudienceDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Settings",
      render: r => /*#__PURE__*/React.createElement(SettingsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Settings/ActivityLog",
      render: r => /*#__PURE__*/React.createElement(ActivityLogViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Settings/Brand",
      render: r => /*#__PURE__*/React.createElement(BrandKitViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Settings/Templates",
      render: r => /*#__PURE__*/React.createElement(TemplatesSettingsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Audience/Create",
      render: r => /*#__PURE__*/React.createElement(AudienceCreateViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Product",
      render: r => /*#__PURE__*/React.createElement(ProductViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Product/Update",
      render: r => /*#__PURE__*/React.createElement(UpdateProductViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Product/:range",
      render: r => /*#__PURE__*/React.createElement(ProductViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Modules/:id",
      render: r => /*#__PURE__*/React.createElement(ModulePage, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity",
      render: r => section('marketing', /*#__PURE__*/React.createElement(WebActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/SiteActivity",
      render: r => section('marketing', /*#__PURE__*/React.createElement(WebActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/:view(overview|acquisition|pages|products|places)",
      render: r => section('marketing', /*#__PURE__*/React.createElement(WebActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/legacy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/legacy/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Detail/:type/:name/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(WebActivityDetailComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/legacy/Detail/:type/:name/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Funnels",
      render: r => section('marketing', /*#__PURE__*/React.createElement(FunnelsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Funnels/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(FunnelsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Funnels/:range/:id",
      render: r => section('marketing', /*#__PURE__*/React.createElement(FunnelsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Heat",
      render: r => section('marketing', /*#__PURE__*/React.createElement(HeatViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Heat/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(HeatViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Pages/Location/:range/:comp/:groupBy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityPagesViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Pages/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityPagesViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Pages/:range/:comp",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityPagesViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Product/Location/:range/:comp/:groupBy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityProductViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Product/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityProductViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Product/:range/:comp",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityProductViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Location/Places/:range/:comp/:groupBy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityLocationViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Location/:range/:comp/:groupBy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityLocationViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Utm/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityUtmViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/Utm/:range/:groupBy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityUtmViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/KeyValues/:type/:name/:startDate/:endDate",
      render: r => section('marketing', /*#__PURE__*/React.createElement(WebActivityKeyValuesComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/legacy/KeyValues/:type/:name/:startDate/:endDate",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityKeyValuesViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Activity/:range/:groupBy",
      render: r => section('marketing', /*#__PURE__*/React.createElement(ActivityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Locations",
      render: r => /*#__PURE__*/React.createElement(LocationViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Locations/Upload",
      render: r => /*#__PURE__*/React.createElement(UploadLocationViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Product",
      render: r => /*#__PURE__*/React.createElement(ForecastProductViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Product/Detail/:id",
      render: r => /*#__PURE__*/React.createElement(ForecastProductDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Product/Detail/:id/:type",
      render: r => /*#__PURE__*/React.createElement(ForecastProductDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Product/Detail/:id/:type/:groupBy",
      render: r => /*#__PURE__*/React.createElement(ForecastProductDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Similarity",
      render: r => section('sales', /*#__PURE__*/React.createElement(ForecastSimilarityViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Similarity/Detail/:id",
      render: r => section('sales', /*#__PURE__*/React.createElement(ForecastSimilarityDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Similarity/Detail/:id/:type",
      render: r => section('sales', /*#__PURE__*/React.createElement(ForecastSimilarityDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Forecast/Similarity/Detail/:id/:type/:groupBy",
      render: r => section('sales', /*#__PURE__*/React.createElement(ForecastSimilarityDetailViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Company",
      render: r => /*#__PURE__*/React.createElement(CompanyAnalysisViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Company/Detail/:id",
      render: r => /*#__PURE__*/React.createElement(CompanyDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Company/Add",
      render: r => /*#__PURE__*/React.createElement(CompanyAnalysisCreateViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Assistant",
      render: r => /*#__PURE__*/React.createElement(ResearchAssistantViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Assistant/Detail/:id",
      render: r => /*#__PURE__*/React.createElement(ResearchAssistantDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Assistant/Add",
      render: r => /*#__PURE__*/React.createElement(ResearchAssistantCreateViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Trends",
      render: r => /*#__PURE__*/React.createElement(MarketTrendsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Trends/Detail/:id",
      render: r => /*#__PURE__*/React.createElement(MarketTrendsDetailViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Research/Trends/Add",
      render: r => /*#__PURE__*/React.createElement(MarketTrendsCreateViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Sales/Analysis",
      render: r => /*#__PURE__*/React.createElement(SalesAnalysisViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Sales/Analysis/:range",
      render: r => /*#__PURE__*/React.createElement(SalesAnalysisViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Sales/targets",
      render: r => section('sales', /*#__PURE__*/React.createElement(SalesTargetsViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Sales",
      render: r => section('sales', /*#__PURE__*/React.createElement(SalesExecutiveViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Sales/:range",
      render: r => section('sales', /*#__PURE__*/React.createElement(SalesExecutiveViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Sales/:range/:tab(reps|dealers|coverage)",
      render: r => section('sales', /*#__PURE__*/React.createElement(SalesExecutiveViewComponent, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Reports",
      render: r => /*#__PURE__*/React.createElement(ReportsListViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Reports/new",
      render: r => /*#__PURE__*/React.createElement(ReportBuilderViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Reports/edit/:id",
      render: r => /*#__PURE__*/React.createElement(ReportBuilderViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Reports/view/:id",
      render: r => /*#__PURE__*/React.createElement(ReportViewerViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/MarketIntelligence",
      render: r => section('marketing', /*#__PURE__*/React.createElement(MarketIntelligenceView, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/MarketIntelligence/:range",
      render: r => section('marketing', /*#__PURE__*/React.createElement(MarketIntelligenceView, {
        params: r.match.params
      }))
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: ["/Notifications", "/Notifications/settings"],
      render: r => /*#__PURE__*/React.createElement(NotificationsViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Search/:term",
      render: r => /*#__PURE__*/React.createElement(SearchViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/Search/:section/:term",
      render: r => /*#__PURE__*/React.createElement(SearchViewComponent, {
        params: r.match.params
      })
    }), /*#__PURE__*/React.createElement(Route, {
      exact: true,
      path: "/:range",
      render: r => /*#__PURE__*/React.createElement(DashboardViewComponent, {
        params: r.match.params
      })
    }))), /*#__PURE__*/React.createElement(Alert, null), /*#__PURE__*/React.createElement(AssistantPanel, null))));
  }
}

// Adopt an existing SSO session BEFORE the first render so the whole app —
// notably the sidebar logo (account.get().company) — paints with the correct
// account on the first frame. Without this the sidebar rendered before the
// token resolved and showed the stale fallback (e.g. "Robb & Stucky") until a
// manual refresh. trySsoAttach is a fast no-op when already authed, on
// localhost, or when there's no broker session.
(async () => {
  try {
    const attached = await Auth.trySsoAttach();
    // Already signed in → still re-read the broker session so registry
    // edits (tenant modules, features, feed URLs) apply on this boot.
    if (!attached) await Auth.refreshTenantConfig();
  } catch (e) {/* fall through to render */}
  ReactDOM.render(/*#__PURE__*/React.createElement(App, null), document.getElementById("app"));
})();